import Vue from 'vue'
import { Module } from 'vuex'
import {
  PromotionGroupInterface,
  PromotionInterface,
} from '@/models/shop/Promotion.types'
import { ArenaProductVariant } from '@/models/shop/ArenaProduct.types'
import { COIN_TYPES } from '@/constants/coins'
import { Recommendation } from '@/models/shop/CartV2.types'

export const CartItemType = {
  VARIANT: 'variant',
  PROMOTION: 'promotion',
} as const
export type CartItemType = (typeof CartItemType)[keyof typeof CartItemType]

type VariantCartItem = {
  type: typeof CartItemType.VARIANT
  variant: ArenaProductVariant
  quantity: number
}

type PromotionGroupSelection = {
  currency: string
  id: string
  image: string
  name: string
  price: string
}

export type PromotionCartItem = {
  type: typeof CartItemType.PROMOTION
  promotion: PromotionInterface
  quantity: number
  groupSelections?: Record<string, PromotionGroupSelection[]>
}

export type CartItem = VariantCartItem | PromotionCartItem

export interface SelectedCustomer {
  id: number | bigint | string
  username: string
  name?: string
  email?: string
  coins: number
  experience: {
    rank: {
      name: string
    }
  }
  topProducts: Array<{
    name: string
    quantity: number
  }>
  topGames: Array<{
    name: string
    playtime: string
  }>
  returningUser: boolean
  offerStarterPack: boolean
}

export interface CartState {
  cartId: string
  cartItems: CartItem[]
  cartItemsSnapshot: CartItem[] | null
  allCatalogVariants: ArenaProductVariant[]
  selectedRecommendation: Recommendation | null
  isCartLoading: boolean
  selectedCustomer: SelectedCustomer | null
}

export interface UpdateVariantQuantityPayload {
  variant: ArenaProductVariant
  quantity: number
}

export interface UpdatePromotionQuantityPayload {
  promotion: PromotionInterface
  quantity: number
}

function isGroupCoins(
  group: PromotionGroupInterface,
  variants: ArenaProductVariant[],
): boolean {
  if (!group?.variants?.length || !variants.length) return false

  const groupVariantIds = new Set(
    group.variants.map((gv) => gv.arena_variant_id),
  )

  return variants.some(
    (variant) =>
      groupVariantIds.has(variant.id) &&
      variant?.attributes?.type === COIN_TYPES.coins,
  )
}

function isPromotionItemComplete(
  item: PromotionCartItem,
  variants: ArenaProductVariant[] = [],
): boolean {
  const { groups } = item.promotion
  const selections = item.groupSelections || {}

  return groups.every((group) => {
    const groupSelections = selections[group.id]
    if (!Array.isArray(groupSelections)) return false

    const validSelections = groupSelections.filter((sel) => !!sel)
    if (!validSelections.length) return false

    const isCoins = isGroupCoins(group, variants)
    if (isCoins) return true

    const quantity = Number(group.quantity) || 1
    return validSelections.length === quantity
  })
}

function findVariantItem(
  items: CartItem[],
  variantId: string | number,
): VariantCartItem | undefined {
  return items.find(
    (item): item is VariantCartItem =>
      item.type === CartItemType.VARIANT && item.variant.id === variantId,
  )
}

function findPromotionItem(
  items: CartItem[],
  promotionId: string | number,
): PromotionCartItem | undefined {
  return items.find(
    (item): item is PromotionCartItem =>
      item.type === CartItemType.PROMOTION && item.promotion.id === promotionId,
  )
}

function preserveGroupSelections(
  existing: PromotionCartItem,
  newItem: PromotionCartItem,
): PromotionCartItem {
  return existing.groupSelections && !newItem.groupSelections
    ? { ...newItem, groupSelections: existing.groupSelections }
    : newItem
}

function findItemIndex(items: CartItem[], item: CartItem): number {
  return items.findIndex((existing) => {
    if (
      existing.type === item.type &&
      existing.type === CartItemType.VARIANT &&
      item.type === CartItemType.VARIANT
    ) {
      return existing.variant.id === item.variant.id
    }
    if (
      existing.type === item.type &&
      existing.type === CartItemType.PROMOTION &&
      item.type === CartItemType.PROMOTION
    ) {
      return existing.promotion.id === item.promotion.id
    }
    return false
  })
}

function filterByType<T extends CartItem>(
  items: CartItem[],
  type: CartItemType,
): T[] {
  return items.filter((item) => item.type === type) as T[]
}

// Fresh copy of the cart module state. Used to seed the module and to fully
// reset it on logout (unlike `resetCart`, this also clears `allCatalogVariants`
// and `isCartLoading`).
function getDefaultCartState(): CartState {
  return {
    cartId: '',
    cartItems: [],
    cartItemsSnapshot: null,
    allCatalogVariants: [],
    selectedRecommendation: null,
    isCartLoading: false,
    selectedCustomer: null,
  }
}

const cartModule: Module<CartState, unknown> = {
  namespaced: true,
  state: getDefaultCartState(),
  mutations: {
    resetCartState(state: CartState): void {
      Object.assign(state, getDefaultCartState())
    },
    setCartId(state: CartState, cartId: string): void {
      state.cartId = cartId
    },
    setCartItems(state: CartState, items: CartItem[]): void {
      const existingVariants = filterByType<VariantCartItem>(
        state.cartItems,
        CartItemType.VARIANT,
      )
      const existingPromotions = filterByType<PromotionCartItem>(
        state.cartItems,
        CartItemType.PROMOTION,
      )
      const incomingVariants = filterByType<VariantCartItem>(
        items,
        CartItemType.VARIANT,
      )
      const incomingPromotions = filterByType<PromotionCartItem>(
        items,
        CartItemType.PROMOTION,
      )

      const mergedVariants =
        incomingVariants.length > 0 ? incomingVariants : existingVariants

      const mergedPromotions =
        incomingPromotions.length > 0
          ? incomingPromotions.map((newItem) => {
              const existingItem = findPromotionItem(
                existingPromotions,
                newItem.promotion.id,
              )
              return existingItem
                ? preserveGroupSelections(existingItem, newItem)
                : newItem
            })
          : existingPromotions

      state.cartItems = [...mergedVariants, ...mergedPromotions]
    },
    setAllCatalogVariants(
      state: CartState,
      variants: ArenaProductVariant[],
    ): void {
      state.allCatalogVariants = variants
    },
    addCartItem(state: CartState, item: CartItem): void {
      const index = findItemIndex(state.cartItems, item)

      if (index === -1) {
        state.cartItems.push(item)
      } else {
        const existing = state.cartItems[index]
        if (
          item.type === CartItemType.PROMOTION &&
          existing.type === CartItemType.PROMOTION
        ) {
          state.cartItems.splice(
            index,
            1,
            preserveGroupSelections(existing, item),
          )
        } else {
          state.cartItems.splice(index, 1, item)
        }
      }
    },
    updateCartItemQuantity(
      state: CartState,
      payload: { variant: ArenaProductVariant; quantity: number },
    ): void {
      const item = findVariantItem(state.cartItems, payload.variant.id)
      if (item) {
        item.quantity = payload.quantity
      }
    },
    removeCartItem(state: CartState, variant: ArenaProductVariant): void {
      state.cartItems = state.cartItems.filter(
        (item) =>
          item.type !== CartItemType.VARIANT || item.variant.id !== variant.id,
      )
    },
    removeCartPromotion(state: CartState, promotion: PromotionInterface): void {
      state.cartItems = state.cartItems.filter(
        (item) =>
          item.type !== CartItemType.PROMOTION ||
          item.promotion.id !== promotion.id,
      )
    },
    removeCartVariantByVariantId(
      state: CartState,
      variantId: string | number,
    ): void {
      state.cartItems = state.cartItems.filter(
        (it) => it.type !== CartItemType.VARIANT || it.variant.id !== variantId,
      )
    },
    updateCartPromotionSelection(
      state: CartState,
      payload: {
        promotion: PromotionInterface
        selections: PromotionCartItem['groupSelections']
      },
    ): void {
      const item = findPromotionItem(state.cartItems, payload.promotion.id)
      if (item) {
        Vue.set(item, 'groupSelections', payload.selections)
      }
    },
    updateCartPromotionQuantity(
      state: CartState,
      payload: { promotion: PromotionInterface; quantity: number },
    ): void {
      const item = findPromotionItem(state.cartItems, payload.promotion.id)
      if (item) {
        item.quantity = payload.quantity
      }
    },
    clearCartItems(state: CartState): void {
      state.cartItems = []
    },
    clearCartId(state: CartState): void {
      state.cartId = ''
    },
    setSelectedRecommendation(
      state: CartState,
      recommendation: Recommendation | null,
    ): void {
      state.selectedRecommendation = recommendation
    },
    setCartLoading(state: CartState, isLoading: boolean): void {
      state.isCartLoading = isLoading
    },
    setSelectedCustomer(
      state: CartState,
      customer: SelectedCustomer | null,
    ): void {
      state.selectedCustomer = customer
    },
    clearSelectedCustomer(state: CartState): void {
      state.selectedCustomer = null
    },
    saveCartSnapshot(state: CartState): void {
      state.cartItemsSnapshot = JSON.parse(JSON.stringify(state.cartItems))
    },
    restoreCartSnapshot(state: CartState): void {
      if (state.cartItemsSnapshot) {
        state.cartItems = JSON.parse(JSON.stringify(state.cartItemsSnapshot))
        state.cartItemsSnapshot = null
      }
    },

    clearCartSnapshot(state: CartState): void {
      state.cartItemsSnapshot = null
    },
  },
  getters: {
    cartId: (state: CartState): string => state.cartId,
    cartItems: (state: CartState): CartItem[] => state.cartItems,
    allCatalogVariants: (state: CartState): ArenaProductVariant[] =>
      state.allCatalogVariants,
    isPromotionSelectionComplete:
      (state: CartState) =>
      (promotionId: string): boolean => {
        const item = findPromotionItem(state.cartItems, promotionId)
        return item
          ? isPromotionItemComplete(item, state.allCatalogVariants)
          : false
      },
    isEveryPromotionCompletelySelected: (state: CartState): boolean => {
      const promotionItems = filterByType<PromotionCartItem>(
        state.cartItems,
        CartItemType.PROMOTION,
      )
      return (
        promotionItems.length === 0 ||
        promotionItems.every((item) =>
          isPromotionItemComplete(item, state.allCatalogVariants),
        )
      )
    },
    isCartLoading: (state: CartState): boolean => state.isCartLoading,
    selectedCustomer: (state: CartState): SelectedCustomer | null =>
      state.selectedCustomer,
    selectedCustomerId: (state: CartState): number | null => {
      return state.selectedCustomer?.id
        ? Number(state.selectedCustomer.id)
        : null
    },
  },
  actions: {
    updateVariantQuantity(
      { commit, state },
      payload: UpdateVariantQuantityPayload,
    ): void {
      const { variant, quantity } = payload
      if (!variant || quantity == null) return

      commit('saveCartSnapshot')

      if (quantity <= 0) {
        if (findVariantItem(state.cartItems, variant.id)) {
          commit('removeCartItem', variant)
        }
        commit('clearCartSnapshot')
        return
      }

      const existingItem = findVariantItem(state.cartItems, variant.id)
      if (existingItem) {
        commit('updateCartItemQuantity', { variant, quantity })
      } else {
        commit('addCartItem', {
          type: CartItemType.VARIANT,
          variant,
          quantity,
        })
      }
    },
    updatePromotionQuantity(
      { commit, state },
      payload: UpdatePromotionQuantityPayload,
    ): void {
      const { promotion, quantity } = payload
      if (!promotion || quantity == null) return

      commit('saveCartSnapshot')
      if (quantity <= 0) {
        if (findPromotionItem(state.cartItems, promotion.id)) {
          commit('removeCartPromotion', promotion)
        }
        commit('clearCartSnapshot')
        return
      }

      const existingItem = findPromotionItem(state.cartItems, promotion.id)
      const promotionItem: PromotionCartItem = {
        type: CartItemType.PROMOTION,
        promotion,
        quantity,
        ...(existingItem?.groupSelections && {
          groupSelections: existingItem.groupSelections,
        }),
      }

      if (existingItem) {
        commit('updateCartPromotionQuantity', { promotion, quantity })
      } else {
        commit('addCartItem', promotionItem)
      }
    },
    setPromotionSelection(
      { commit, state },
      payload: {
        promotion: PromotionInterface
        selections: PromotionCartItem['groupSelections']
      },
    ): void {
      const { promotion, selections } = payload
      if (!promotion) return

      const existingItem = findPromotionItem(state.cartItems, promotion.id)
      if (!existingItem) {
        commit('addCartItem', {
          type: CartItemType.PROMOTION,
          promotion,
          quantity: 1,
          groupSelections: selections,
        })
        return
      }

      commit('updateCartPromotionSelection', { promotion, selections })
    },
    removeVariant({ commit }, variant: ArenaProductVariant): void {
      commit('saveCartSnapshot')
      commit('removeCartItem', variant)
    },
    removePromotion({ commit }, promotion: PromotionInterface): void {
      commit('saveCartSnapshot')
      commit('removeCartPromotion', promotion)
    },
    resetCart({ commit }): void {
      commit('clearCartItems')
      commit('clearCartId')
      commit('setSelectedRecommendation', null)
      commit('clearSelectedCustomer')
      commit('clearCartSnapshot')
    },
    setSelectedRecommendation(
      { commit },
      recommendation: Recommendation | null,
    ): void {
      commit('setSelectedRecommendation', recommendation)
    },
    setCartLoading({ commit }, isLoading: boolean): void {
      commit('setCartLoading', isLoading)
    },
    setSelectedCustomer({ commit }, customer: SelectedCustomer | null): void {
      commit('setSelectedCustomer', customer)
    },
    clearSelectedCustomer({ commit }): void {
      commit('clearSelectedCustomer')
    },
    clearSnapshot({ commit }): void {
      commit('clearCartSnapshot')
    },

    // Add this helper to rollback on error
    rollbackCart({ commit }): void {
      commit('restoreCartSnapshot')
    },
  },
}

export default cartModule
